/**
 * hermesEmbed — shared plumbing for embedding the same-origin Hermes dashboard.
 *
 * Two surfaces consume this:
 *   - HermesDashboardTile — the full Hermes GUI on the Terminals page (Part A).
 *   - HermesChatFrame — a Hermes *chat* tab inside the Pi chat dock (per-tab
 *     kind; the /chat route only).
 *
 * Both ride the vibe-backend reverse proxy (routes/hermes_dashboard.py), so
 * they share: the same-origin proxy subtree, the authorize step that mints the
 * short-lived cookie the iframe's browser sub-requests present, and the GHL
 * theme injection (Part B) — hermes derives its colors via color-mix() from
 * base layers we override with a !important <style> in its contentDocument.
 */
import { api } from '@/services/api';
import { GHL_THEMES, hermesThemeCss, type GhlThemeId } from '@/shared/theme/agentThemes';

/** Same-origin proxy subtree — must match PROXY_PREFIX in the backend. */
export const HERMES_APP_SRC = '/api/hermes-dashboard/app/';
/**
 * Deep-link to the hermes embedded chat. Hermes uses BrowserRouter with
 * basename = X-Forwarded-Prefix (our proxy prefix), so the chat route is the
 * path `…/app/chat` (NOT a hash). Embedded chat is always enabled server-side
 * (isDashboardEmbeddedChatEnabled === true) and uses /api/pty + /api/ws, which
 * the proxy's WS catch-all already handles.
 */
export const HERMES_CHAT_SRC = `${HERMES_APP_SRC}chat`;
/** Re-mint at ~half the backend COOKIE_TTL_SECONDS (3600s) so it never lapses. */
export const HERMES_REAUTHORIZE_INTERVAL_MS = 25 * 60 * 1000;
/** Style element id injected into the hermes iframe head to hold the GHL theme. */
export const HERMES_THEME_STYLE_ID = 'vibe-agent-theme';

/** POST the authorize endpoint (Bearer via services/api → Set-Cookie for the iframe). */
export async function authorizeHermesDashboard(): Promise<void> {
  await api.post('/hermes-dashboard/authorize');
}

/**
 * Inject (or update) the GHL-theme override stylesheet inside the same-origin
 * hermes iframe document. No-op if the doc/head isn't painted yet (the caller's
 * iframe onLoad re-runs this once it is).
 */
export function injectHermesTheme(
  doc: Document | null | undefined,
  themeId: GhlThemeId,
): void {
  if (!doc || !doc.head) return;
  let el = doc.getElementById(HERMES_THEME_STYLE_ID) as HTMLStyleElement | null;
  if (!el) {
    el = doc.createElement('style');
    el.id = HERMES_THEME_STYLE_ID;
    doc.head.appendChild(el);
  }
  el.textContent = hermesThemeCss(GHL_THEMES[themeId]);
}
